import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  MdDashboard,
  MdPeople,
  MdWork,
  MdSettings,
  MdLogout,
} from "react-icons/md";
import {
  FiChevronDown,
  FiChevronUp,
  FiMail,
  FiGlobe,
  FiSliders,
  FiTool,
  FiCreditCard,
  FiActivity,
  FiList,
  FiDatabase,
  FiCpu,
  FiFileText,
  FiZap,
} from "react-icons/fi";
import { FiPackage } from "react-icons/fi";
import { BiSolidGift } from "react-icons/bi";
import { useAuth } from "../context/AuthContext";

const Sidebar: React.FC = () => {
  const { logout } = useAuth();
  const location = useLocation();
  const [settingsExpanded, setSettingsExpanded] = useState(
    location.pathname.startsWith("/settings")
  );

  const menuItems = [
    { path: "/dashboard", icon: MdDashboard, label: "Dashboard" },
    { path: "/users", icon: MdPeople, label: "Users" },
    { path: "/subscriptions", icon: FiCreditCard, label: "Billing" },
    { path: "/jobs", icon: MdWork, label: "Jobs" },
    { path: "/cvs", icon: FiFileText, label: "CVs" },
    { path: "/usage", icon: FiActivity, label: "Usage" },
    { path: "/talent-finder", icon: FiZap, label: "Talent Finder" },
    { path: "/guest-usage", icon: MdPeople, label: "Guest Usage" },
    { path: "/activity", icon: FiList, label: "Activity Logs" },
  ];

  const settingsSubItems = [
    { path: "/settings/app", icon: FiSliders, label: "App Settings" },
    { path: "/settings/weight-templates", icon: FiSliders, label: "Weight Templates" },
    { path: "/settings/contact-billing", icon: FiCreditCard, label: "Contact / Billing" },
    { path: "/settings/master-variables", icon: FiDatabase, label: "Master Variables" },
    { path: "/settings/packages", icon: FiPackage, label: "Packages" },
    { path: "/settings/promo-codes", icon: BiSolidGift, label: "Promo Codes" },
    { path: "/settings/ai-models", icon: FiCpu, label: "AI Settings" },
    {
      path: "/settings/countries-industries",
      icon: FiGlobe,
      label: "Countries & Industries",
    },
    { path: "/settings/emails", icon: FiMail, label: "Emails" },
    { path: "/settings/maintenance", icon: FiTool, label: "Maintenance" },
  ];

  const isSettingsActive = location.pathname.startsWith("/settings");

  return (
    <div className="flex h-screen w-64 flex-col bg-white shadow-lg dark:bg-navy-800">
      {/* Logo */}
      <div className="flex h-20 items-center justify-center border-b border-gray-200 dark:border-navy-700">
        <div className="flex items-center space-x-3">
          <img src="/logo.png" alt="Teamther.ai logo" className="h-18 w-32" />
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1 p-4 overflow-y-auto">
        {menuItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className={({ isActive }) =>
              `flex items-center space-x-3 rounded-lg px-4 py-3 transition ${
                isActive
                  ? "bg-brand-500 text-white shadow-md"
                  : "text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-navy-700"
              }`
            }
          >
            <item.icon className="h-5 w-5" />
            <span className="font-medium">{item.label}</span>
          </NavLink>
        ))}

        {/* Settings Submenu */}
        <div>
          <button
            onClick={() => setSettingsExpanded(!settingsExpanded)}
            className={`flex w-full items-center justify-between rounded-lg px-4 py-3 transition ${
              isSettingsActive
                ? "bg-brand-500 text-white shadow-md"
                : "text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-navy-700"
            }`}
          >
            <div className="flex items-center space-x-3">
              <MdSettings className="h-5 w-5" />
              <span className="font-medium">Settings</span>
            </div>
            {settingsExpanded ? (
              <FiChevronUp className="h-5 w-5" />
            ) : (
              <FiChevronDown className="h-5 w-5" />
            )}
          </button>

          {settingsExpanded && (
            <div className="ml-4 mt-1 space-y-1 border-l-2 border-gray-200 pl-4 dark:border-navy-600">
              {settingsSubItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    `flex items-center space-x-3 rounded-lg px-3 py-2 text-sm transition ${
                      isActive
                        ? "bg-brand-100 text-brand-600 dark:bg-brand-900/30 dark:text-brand-400"
                        : "text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-navy-700"
                    }`
                  }
                >
                  <item.icon className="h-4 w-4" />
                  <span className="font-medium">{item.label}</span>
                </NavLink>
              ))}
            </div>
          )}
        </div>
      </nav>

      {/* Logout Button */}
      <div className="border-t border-gray-200 p-4 dark:border-navy-700">
        <button
          onClick={logout}
          className="flex w-full items-center space-x-3 rounded-lg px-4 py-3 text-red-600 transition hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
        >
          <MdLogout className="h-5 w-5" />
          <span className="font-medium">Logout</span>
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
